import { action, makeObservable } from 'mobx';
import { inject, observer } from 'mobx-react';
import React, { ChangeEvent } from 'react';
import {
  Alert,
  Button,
  Col,
  Card,
  Row,
  Form,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { LinkContainer } from 'react-router-bootstrap';

import DataSetType from '../../../../models/audience/DataSetType';
import InputFileDataSet from '../../../../models/audience/InputFileDataSet';
import Store from '../../../../store';
import { getDatasetParameter } from '../../../../utils';
import Error from '../../../components/error/Error';
import checkPermissions from '../../../components/CheckPermissions';
import { PROFILES_PERMISSIONS } from '../../../../permissions';
import Spinner from '../../../components/Spinner';
import { MAX_NUMBER_OF_LOADING_DATASETS } from '../../../../utils/constants';

interface AudiencePageProps extends RouteComponentProps {
  store?: Store;
  /**
   * Where this is mounted. `page` is the route, unchanged.
   *
   * `modal` drops the chrome a route needs and a dialog supplies for itself: the way
   * back, the page heading and the centred width. The upload, its validation and the
   * concurrent-load limit are the same in both. Mirrors `MkAudiencePage`.
   */
  chrome?: 'page' | 'modal';
  /** Called instead of navigating, once the upload has been accepted. */
  onDone?: () => void;
  /** Called when the reader backs out. Only used when `chrome` is `modal`. */
  onCancel?: () => void;
}

interface AudiencePageState {
  loading: boolean;
  currentLoadingModels: number[];
}

const AudiencePage = inject('store')(
  observer(
    class AudiencePage extends React.Component<
      AudiencePageProps,
      AudiencePageState
    > {
      errorMessageRef: React.RefObject<HTMLDivElement>;

      constructor(props: AudiencePageProps) {
        super(props);

        this.state = { loading: true, currentLoadingModels: [] };

        makeObservable(this, {
          handleDataSetChange: action,
          handleOnClickDownload: action,
        });

        this.handleDataSetChange = this.handleDataSetChange.bind(this);
        this.uploadDataSetsFiles = this.uploadDataSetsFiles.bind(this);
        this.handleOnClickDownload = this.handleOnClickDownload.bind(this);

        this.errorMessageRef = React.createRef();
      }

      async componentDidMount() {
        const { tenant } = this.props.store;
        const [currentLoadingModels] = await Promise.all([
          this.props.store.mkAudiencePage.getCurrentLoadingModels(tenant),
          this.props.store.fetchForCsvFile(),
        ]);

        this.setState({ loading: false, currentLoadingModels });
      }

      componentDidUpdate(): void {
        const {
          csvPage: { dataSets },
        } = this.props.store;

        if (dataSets.uploadDataSetError) {
          this.errorMessageRef.current.scrollIntoView();
        }
      }

      async uploadDataSetsFiles() {
        const dataset: DataSetType = getDatasetParameter(
          this.props.location.search
        );
        await this.props.store.uploadDataSetsFiles(dataset);
        const { tenant, modelId, csvPage } = this.props.store;
        if (csvPage.dataSets.uploadDataSetError) return;
        // A dialog closes; a route navigates. Both mean the upload was accepted.
        if (this.props.onDone) {
          this.props.onDone();
          return;
        }
        this.props.history.push(`/tenant/${tenant}/models/${modelId}/overview`);
      }

      handleDataSetChange(event: ChangeEvent<HTMLInputElement>, type: string) {
        const field = this.props.store.csvPage.dataSets.inputFileDataSets.find(
          (d) => d.type === type
        );
        field.file = event.target.files[0];
      }

      async handleOnClickDownload() {
        const { tenant, modelId } = this.props.store;
        await this.props.store.csvPage.downloadCsvFile(tenant, modelId);
      }

      renderAlert() {
        const {
          isDownloadedSuccessfully,
          isOldFileFound,
          isErrorOccurred,
          isDownloading,
        } = this.props.store.csvPage;
        if (isOldFileFound) {
          if (isDownloadedSuccessfully) {
            return (
              <Alert variant="success">
                The actual training dataset has been downloaded successfully.
              </Alert>
            );
          }
          if (isErrorOccurred) {
            return (
              <Alert variant="danger">
                Something went wrong while downloading your file.
              </Alert>
            );
          }
          return (
            <Alert variant="warning">
              <Row>
                <Col xs={9}>
                  A training dataset file has been found, click here to
                  download.
                </Col>
                <Col>
                  <Button
                    variant="primary"
                    disabled={isDownloading}
                    onClick={this.handleOnClickDownload}
                  >
                    {isDownloading ? 'Downloading...' : 'Download'}
                  </Button>
                </Col>
              </Row>
            </Alert>
          );
        }
        return <Alert variant="warning">No old csv file found.</Alert>;
      }

      render() {
        const { csvPage, isAllowedToEdit, tenant, modelId } = this.props.store;
        const { loading, currentLoadingModels } = this.state;
        const { dataSets } = csvPage;
        const dataset: DataSetType = getDatasetParameter(
          this.props.location.search
        );
        const inModal = this.props.chrome === 'modal';
        // Words are the one difference a stylesheet cannot carry. Every call reads
        // "dialog wording, then exactly what the route said before".
        const t = (dialog: React.ReactNode, page: React.ReactNode) =>
          inModal ? dialog : page;
        const dataSetsValid = dataSets.inputFileDataSets.find(
          (t) => t.type === (dataset || 'training')
        ).file;

        // Full width, under its own label. It was a `File:` in bold beside a control
        // four columns wide, which left the file name, the one thing a person checks
        // after choosing, clipped in a third of the card.
        // The same control either way; only its label and its width differ. The route
        // keeps `File:` beside a four-column input, which clips the file name, because
        // fixing that is a visible change and this page is behind no flag.
        const fileControl = (set: InputFileDataSet) => (
          <Form.Control
            type="file"
            disabled={!isAllowedToEdit}
            accept=".csv"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              this.handleDataSetChange(e, set.type)
            }
          />
        );

        const getFieldInput = (set: InputFileDataSet) =>
          t(
            <Form.Group controlId={`field_file_${set.type}`} key={set.type}>
              <Form.Label className="dsf-label">CSV file</Form.Label>
              {fileControl(set)}
            </Form.Group>,
            <Form.Group
              as={Row}
              controlId={`field_file_${set.type}`}
              key={set.type}
            >
              <Col sm="auto">
                <Form.Label className="pt-1 fw-bold">File:</Form.Label>
              </Col>
              <Col sm={4}>{fileControl(set)}</Col>
            </Form.Group>
          );

        const cantLoad =
          currentLoadingModels.length >= MAX_NUMBER_OF_LOADING_DATASETS;

        if (loading) {
          return <Spinner />;
        }

        return (
          <>
            {!inModal && (
              <LinkContainer
                to={`/tenant/${tenant}/models/${modelId}/overview`}
                activeClassName=""
              >
                <Button variant="link" className="me-2">
                  Go back to Overview
                </Button>
              </LinkContainer>
            )}
            <div
              // `dsf` only in the dialog. Every rule in datasetForm.css sits under
              // that class, so on the route, which is mounted for every tenant with
              // no flag, none of them match and the page renders as it did.
              className={
                inModal ? 'dsf d-grid gap-4' : 'w-75 mt-4 mx-auto d-grid gap-4'
              }
            >
              {/* The dialog's own title says this already. */}
              {!inModal && <h4>Edit model datasets</h4>}
              {this.renderAlert()}
              {/* The required columns, as the columns they are.

                  Each card opened with a paragraph and then a bullet list, five bullets
                  on the validation side, to name five column headings. That is a lot of
                  prose to say "this file needs these five columns", and the thing a
                  reader is checking against is their spreadsheet's header row, which is
                  a list of short names, not sentences. */}
              <div className="dsf-pair">
                <Card className="mb-3" border="light">
                  <Card.Body>
                    <Card.Title>
                      {dataset
                        ? String(dataset).charAt(0).toUpperCase() +
                          dataset.slice(1)
                        : 'Training'}
                    </Card.Title>
                    <p className="mb-4">
                      Contacts who converted and who did not. The segmentation
                      is built from these.
                    </p>
                    {t(
                      <>
                        {t(
                          <>
                            <div className="dsf-cols">
                              <span className="dsf-cols-label">
                                Required columns
                              </span>
                              <code className="dsf-col">email</code>
                              <code className="dsf-col">target</code>
                            </div>
                            <p className="dsf-cols-note">
                              <b>target</b> is 1 when the record converted and 0
                              when it did not. For a list of domains, prefix
                              each one with
                              <b> madkudu@</b>.
                            </p>
                          </>,
                          <>
                            <p>
                              The <b>validation dataset</b> must contain the
                              following columns
                            </p>
                            <ul>
                              <li>
                                email: contains the email of the record. If
                                using a list of domains append
                                &ldquo;madkudu@&rdquo; to the domains.
                              </li>
                              <li>
                                target: value of 1 if the record has converted,
                                0 otherwise
                              </li>
                              <li>
                                amount: if the record has converted, value of
                                the opportunity
                              </li>
                              <li>
                                target_closed_won: Values 1 or 0 to identify if
                                the records converted into a Closed Won
                                opportunity
                              </li>
                              <li>
                                amount_closed_won: if converted into a Closed
                                Won opportunity, the amount value of the deal
                              </li>
                            </ul>
                            <p>
                              This will allow evaluating the impact of the model
                              on the revenue pipeline
                            </p>
                          </>
                        )}
                      </>,
                      <>
                        <p>
                          The <b>training dataset</b> must contain two columns:
                          &lsquo;email&rsquo; and &lsquo;target&rsquo;.
                        </p>
                        <ul>
                          <li>
                            email: contains the email of the record. If using a
                            list of domains append &ldquo;madkudu@&rdquo; to the
                            domains.
                          </li>
                          <li>
                            target: value of 1 if the record has converted, 0
                            otherwise
                          </li>
                        </ul>
                      </>
                    )}
                    {dataSets.uploadDataSetError && (
                      <div ref={this.errorMessageRef}>
                        <Error
                          message={dataSets.uploadDataSetError.message}
                          type={dataSets.uploadDataSetError.type}
                        />
                      </div>
                    )}
                    {getFieldInput(
                      dataSets.inputFileDataSets.find(
                        (t) => t.type === (dataset || 'training')
                      )
                    )}
                  </Card.Body>
                </Card>

                {!dataset && (
                  <Card className="mb-3" border="light">
                    <Card.Body>
                      <Card.Title>
                        {t('Validation', 'Validation dataset')}
                      </Card.Title>
                      <p className="mb-4">
                        A different period, to check the segmentation holds
                        outside what it was built on.
                      </p>
                      <div className="dsf-cols">
                        <span className="dsf-cols-label">Required columns</span>
                        <code className="dsf-col">email</code>
                        <code className="dsf-col">target</code>
                        <code className="dsf-col">amount</code>
                        <code className="dsf-col">target_closed_won</code>
                        <code className="dsf-col">amount_closed_won</code>
                      </div>
                      <p className="dsf-cols-note">
                        The amounts are what let the model be measured against
                        pipeline rather than against record counts:
                        <b> amount</b> for a converted record,
                        <b> amount_closed_won</b> for one that closed.
                      </p>
                      {getFieldInput(
                        dataSets.inputFileDataSets.find(
                          (t) => t.type === 'validation'
                        )
                      )}
                    </Card.Body>
                  </Card>
                )}
              </div>
              {/* Same split as the parameters form: Save stays where its disabled
                  logic and tooltip already live, and Cancel is the one control whose
                  meaning differs, closing here and going back to the picker there. */}
              <div className={inModal ? 'mb-2' : 'mx-auto mb-4'}>
                {inModal ? (
                  <Button
                    variant="outline-primary"
                    className="me-2"
                    onClick={this.props.onCancel}
                  >
                    Cancel
                  </Button>
                ) : (
                  <LinkContainer
                    to={`/tenant/${tenant}/models/${modelId}/import_data`}
                    activeClassName=""
                  >
                    <Button variant="outline-primary" className="me-2">
                      Cancel
                    </Button>
                  </LinkContainer>
                )}

                {checkPermissions(
                  PROFILES_PERMISSIONS.ARCHITECT,
                  <OverlayTrigger
                    show={cantLoad}
                    overlay={
                      <Tooltip>
                        {`Model ${currentLoadingModels[0]} and ${currentLoadingModels[1]} are currently loading. Please wait until they both load to begin loading another model`}
                      </Tooltip>
                    }
                  >
                    <span>
                      <Button
                        variant="primary"
                        disabled={
                          !dataSetsValid || !isAllowedToEdit || cantLoad
                        }
                        onClick={this.uploadDataSetsFiles}
                      >
                        Save audience
                      </Button>
                    </span>
                  </OverlayTrigger>
                )}
              </div>
            </div>
          </>
        );
      }
    }
  )
);

export default withRouter(AudiencePage);
